import "twin.macro";

import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { Tab, Tabs } from "@mui/material";

import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import { SaveFormBanner } from "@features/ui";
import { UserPayload, useUpdateUserMutation } from "@features/users/data";
import { Distributor, DistributorOrganization } from "@models/Distributor";
import asyncPool from "@utility/asyncPool";
import useRoleIs from "@utils/useRoleIs";

import {
  useUpdateDistributorMutation,
  useUpdateDistributorOrganizationMutation,
} from "../data";
import DistributorCheckoutOptionsWarning from "./DistributorCheckoutOptionsWarning";
import DistributorOrgForm from "./DistributorOrgForm";
import DistributorUsersForm from "./DistributorUsersForm";

type DistributorFormProps = {
  distributor: Distributor;
};
const formValuesFromDistributor = (distributor: Distributor) => {
  return {
    distributorOrganizations: Object.fromEntries(
      distributor.distributorOrganizations.map((distOrg) => [
        distOrg.organization.id,
        {
          name: distOrg.organization.name,
          allowBudgetCheckout: distOrg.allowBudgetCheckout,
          allowStripeCheckout: distOrg.allowStripeCheckout,
          allowStandardCheckout: distOrg.allowStandardCheckout,
          channelIds: distOrg.channelIds.map(String),
          territoryIds: distOrg.territoryIds.map(String),
          brandIds: distOrg.brands.map((brand) => brand.id),
        },
      ])
    ),
    orderers: Object.fromEntries(
      distributor.orderers.map((orderer) => [
        orderer.id,
        {
          limitedBrands: orderer.limitedBrands.map((brand) => brand.id),
          distributorOrganizationIds: orderer.distributorOrganizationIds,
          territoryIds: orderer.territories.map((t) => t.id),
          channelIds: orderer.channelUsers.map((cu) => cu.channel.id),
        },
      ])
    ),
  };
};

export type FormData = ReturnType<typeof formValuesFromDistributor>;

const DistributorForm = ({ distributor }: DistributorFormProps) => {
  const roleIs = useRoleIs();
  const organizationId = useSelector(
    (state: any) => state.currentUser.organization.id
  );
  const [isSaving, setIsSaving] = useState(false);
  const updateDistributorMutation = useUpdateDistributorMutation();
  const updateUserMutation = useUpdateUserMutation();
  const updateDistributorOrganizationMutation =
    useUpdateDistributorOrganizationMutation();
  const [distributorOrg, setDistributorOrg] =
    useState<DistributorOrganization | null>(
      distributor.distributorOrganizations.find(
        (org) => org.organization.id === organizationId
      ) ?? null
    );

  const formMethods = useForm({
    defaultValues: formValuesFromDistributor(distributor),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    getFieldState,
    reset,
  } = formMethods;

  // Handles setting distributorOrg to a valid org if the current selected org is removed.
  useEffect(() => {
    if (
      !distributorOrg ||
      !distributor.distributorOrganizations.some(
        (org) => org.id === distributorOrg.id
      )
    ) {
      setDistributorOrg(distributor.distributorOrganizations[0]);
    }
  }, [distributorOrg, distributor.distributorOrganizations]);

  const onSubmit = async (data: FormData) => {
    setIsSaving(true);
    try {
      // Update the distributor organizations
      const distOrgPayloads = distributor.distributorOrganizations
        .filter(
          ({ organization }) =>
            getFieldState(`distributorOrganizations.${organization.id}`).isDirty
        )
        .map((distOrg) => ({
          id: distOrg.id,
          distributorId: distributor.id,
          organizationId: distOrg.organization.id,
          ...data.distributorOrganizations[distOrg.organization.id],
        }));
      await asyncPool(3, distOrgPayloads, (payload) =>
        updateDistributorOrganizationMutation.mutateAsync(payload)
      );

      // Update the users
      const userPayloads = distributor.orderers
        .filter((orderer) => getFieldState(`orderers.${orderer.id}`).isDirty)
        .map((orderer) => {
          const userFormValues = data.orderers[orderer.id];
          return {
            ...orderer,
            channels: userFormValues.channelIds.map((id) => ({ id })),
            territories: userFormValues.territoryIds.map((id) => ({ id })),
            brands: userFormValues.limitedBrands.map((id) => ({
              id,
              name: "",
            })),
            distributorOrganizationIds:
              userFormValues.distributorOrganizationIds.map(String),
          } satisfies UserPayload;
        });

      await asyncPool(3, userPayloads, (payload) =>
        updateUserMutation.mutateAsync(payload)
      );

      // Update the distributor
      updateDistributorMutation.mutate(
        {
          id: distributor.id,
          ...data,
          ordererIds: Object.keys(data.orderers),
        },
        // Do a hard form reset.
        { onSuccess: (dist) => handleHardReset(dist) }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleHardReset = (dist: Distributor) =>
    reset(formValuesFromDistributor(dist));

  // Updates the form defaultValues without resetting the dirty values
  // Used when a user-update invalidates the distributor (such as when an admin user removes or adds a user to the distributor)
  useEffect(() => {
    reset(formValuesFromDistributor(distributor), {
      keepDirtyValues: true,
      keepErrors: true,
    });
  }, [distributor, reset]);

  return (
    <FormProvider {...formMethods}>
      {isDirty && (
        <SaveFormBanner
          handleReset={() => handleHardReset(distributor)}
          handleSave={handleSubmit(onSubmit)}
          isLoading={isSaving}
        />
      )}
      <DistributorCheckoutOptionsWarning />
      <OpaqueCard tw="p-0 divide-y divide-neutral-200">
        {roleIs("super") && (
          <Tabs
            value={distributorOrg?.id ?? ""}
            onChange={(e, value) => {
              setDistributorOrg(
                distributor.distributorOrganizations.find(
                  (org) => org.id === value
                )!
              );
            }}
            indicatorColor="secondary"
          >
            {_.sortBy(
              distributor.distributorOrganizations,
              (org) => org.organization.name
            ).map((distOrg) => (
              <Tab
                key={distOrg.id}
                label={distOrg.organization.name}
                value={distOrg.id}
              />
            ))}
          </Tabs>
        )}
        {distributorOrg && (
          <div tw="px-6 pb-6">
            <DistributorOrgForm distributorOrg={distributorOrg.organization} />
          </div>
        )}
      </OpaqueCard>
      {distributorOrg && (
        <DistributorUsersForm
          distributorOrg={distributorOrg.organization}
          orderers={distributor.orderers}
        />
      )}
    </FormProvider>
  );
};

export default DistributorForm;
